export class ServerUrls {
  private readonly prefix: string;

  public constructor(queryString?: string) {
    if (queryString && queryString.length > 0) {
      // Prepend a '?' if missing
      queryString = (queryString[0] === '?' ? '' : '?') + queryString;
    } else {
      queryString = '';
    }
    this.prefix = '/' + queryString + '#';
  }

  // The full url is prefixed by the query string and puts the path in the hash.
  private fullUrl(path: string): string {
    return this.prefix + path;
  }

  public serverPath(path: string): string {
    return '/server' + path;
  }

  public sitesUrl(): string {
    return this.fullUrl(this.sitesPath());
  }

  public sitesPath(): string {
    return this.serverPath('/sites');
  }

  // Users

  public usersUrl(): string {
    return this.fullUrl(this.usersPath());
  }

  public usersPath(): string {
    return this.serverPath('/users');
  }

  // While the user identity migration is in process, we move the users space into the users/list subspace.

  public allUsersListUrl(): string {
    return this.fullUrl(this.allUsersListPath());
  }

  public allUsersListPath(): string {
    return this.usersPath() + '/list';
  }

  // While the user identity migration is in process, we have an identity subspace for users.

  public allUserIdentitiesUrl(): string {
    return this.fullUrl(this.allUserIdentitiesPath());
  }

  public allUserIdentitiesPath(): string {
    return this.usersPath() + '/identities';
  }

  public schedulesUrl(): string {
    return this.fullUrl(this.schedulesPath());
  }

  public schedulesPath(): string {
    return this.serverPath('/schedules');
  }

  public scheduleUrl(scheduleId: string): string {
    return this.fullUrl(this.schedulePath(scheduleId));
  }

  public schedulePath(scheduleId: string): string {
    return this.schedulesPath() + '/' + scheduleId;
  }

  public scheduleExtractRefreshesUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleExtractRefreshesPath(scheduleId));
  }

  public scheduleExtractRefreshesPath(scheduleId: string): string {
    return this.schedulePath(scheduleId) + '/extractRefreshes';
  }

  public scheduleFlowsUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleFlowsPath(scheduleId));
  }

  public scheduleFlowsPath(scheduleId: string): string {
    return this.schedulePath(scheduleId) + '/flows';
  }

  public scheduleSubscriptionsUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleSubscriptionsPath(scheduleId));
  }

  public scheduleSubscriptionsPath(scheduleId: string): string {
    return this.schedulePath(scheduleId) + '/subscriptions';
  }

  public scheduleDataAccelerationUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleDataAccelerationPath(scheduleId));
  }

  public scheduleDataAccelerationPath(scheduleId: string): string {
    return this.schedulePath(scheduleId) + '/dataAcceleration';
  }

  public scheduleDetailsUrl(scheduleId: string): string {
    return this.fullUrl(this.scheduleDetailsPath(scheduleId));
  }

  public scheduleDetailsPath(scheduleId: string): string {
    return this.schedulePath(scheduleId) + '/details';
  }

  public jobsUrl(): string {
    return this.fullUrl(this.jobsPath());
  }

  public jobsPath(): string {
    return this.serverPath('/jobs');
  }

  public tasksUrl(): string {
    return this.fullUrl(this.tasksPath());
  }

  public tasksPath(): string {
    return this.serverPath('/tasks');
  }

  public taskPath(subspaceName: string): string {
    return this.serverPath('/tasks') + subspaceName;
  }

  public tasksExtractRefreshesUrl(): string {
    return this.fullUrl(this.tasksExtractRefreshesPath());
  }

  public tasksExtractRefreshesPath(): string {
    return this.taskPath('/extractRefreshes');
  }

  public tasksFlowsUrl(): string {
    return this.fullUrl(this.tasksFlowsPath());
  }

  public tasksFlowsPath(): string {
    return this.taskPath('/flows');
  }

  public tasksLinkedTasksUrl(): string {
    return this.fullUrl(this.tasksLinkedTasksPath());
  }

  public tasksLinkedTasksPath(): string {
    return this.taskPath('/linkedTasks');
  }

  public tasksDataAccelerationUrl(): string {
    return this.fullUrl(this.tasksDataAccelerationPath());
  }

  public tasksDataAccelerationPath(): string {
    return this.taskPath('/dataAcceleration');
  }

  public tasksSubscriptionsUrl(): string {
    return this.fullUrl(this.tasksSubscriptionsPath());
  }

  public tasksSubscriptionsPath(): string {
    return this.taskPath('/subscriptions');
  }

  public statusUrl(): string {
    return this.fullUrl(this.statusPath());
  }

  public statusPath(): string {
    return this.serverPath('/status');
  }

  public settingsUrl(): string {
    return this.fullUrl(this.settingsPath());
  }

  public settingsPath(): string {
    return this.serverPath('/settings');
  }

  public settingsLicensesUrl(): string {
    return this.fullUrl(this.settingsLicensesPath());
  }

  public settingsLicensesPath(): string {
    return this.serverPath('/licenses');
  }

  public settingsExtensionsUrl(): string {
    return this.fullUrl(this.settingsExtensionsPath());
  }

  public settingsExtensionsPath(): string {
    return this.serverPath('/extensions');
  }

  public addSiteUrl(): string {
    return this.fullUrl(this.addSitePath());
  }

  public addSitePath(): string {
    return this.serverPath('/addSite');
  }

  public settingsCustomizationUrl(): string {
    return this.fullUrl(this.settingsCustomizationPath());
  }

  public settingsCustomizationPath(): string {
    return this.serverPath('/customization');
  }

  public settingsIntegrationsUrl(): string {
    return this.fullUrl(this.settingsIntegrationsPath());
  }

  public settingsIntegrationsPath(): string {
    return this.serverPath('/integrations');
  }

  public settingsResourceLimitsUrl(): string {
    return this.fullUrl(this.settingsResourceLimitsPath());
  }

  public settingsResourceLimitsPath(): string {
    return this.serverPath('/resourceLimits');
  }

  public settingsConnectedApplicationsUrl(): string {
    return this.fullUrl(this.settingsConnectedApplicationsPath());
  }

  public settingsConnectedApplicationsPath(): string {
    return this.serverPath('/connectedApplications');
  }

  public settingsMobileUrl(): string {
    return this.fullUrl(this.settingsMobilePath());
  }

  public settingsMobilePath(): string {
    return this.serverPath('/mobile');
  }

  public userUrl(domainName: string, username: string): string {
    return this.fullUrl(this.userPath(domainName, username));
  }

  public userPath(domainName: string, username: string): string {
    return this.serverPath('/user/' + encodeURIComponent(domainName) + '/' + encodeURIComponent(username));
  }

  public baseUserPath(): string {
    return this.serverPath(ServerUrls.baseUserPart());
  }

  private static baseUserPart(): string {
    return `/user`;
  }

  public userSettingsUrl(domainName: string, username: string): string {
    return this.fullUrl(this.userSettingsPath(domainName, username));
  }

  public userSettingsPath(domainName: string, username: string): string {
    return this.serverPath('/user/' + encodeURIComponent(domainName) + '/' + encodeURIComponent(username) + '/settings');
  }

  // Server Status Vizzes
  public serverStatusVizUrl(sheetUrlId: string): string {
    return this.fullUrl(this.serverStatusVizPath(sheetUrlId));
  }
  public serverStatusVizPath(sheetUrlId: string): string {
    return this.serverPath(ServerUrls.serverStatusVizPart(sheetUrlId));
  }
  private static serverStatusVizPart(sheetUrlId: string): string {
    return `/analysis/${sheetUrlId}`;
  }
}
